import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ResearchEvidence, ResearchMessage } from '@cdf/api-client-react';

import { Badge } from '@/components/ui/badge';

function safeHref(href: string | undefined): string | undefined {
  if (!href) return undefined;
  if (href.startsWith('#') || (href.startsWith('/') && !href.startsWith('//') && !href.includes('\\'))) return href;
  try {
    const url = new URL(href);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

function citationMarkdown(content: string, evidence: ResearchEvidence[]): string {
  return content.replace(/\[(\d+)](?!\()/g, (match, rawIndex: string) => {
    const index = Number(rawIndex) - 1;
    const source = evidence[index];
    const href = safeHref(source?.authorizedLocation ?? undefined);
    return source && href ? `[${match}](${href})` : match;
  });
}

export function ResearchMarkdown({ content, evidence = [], verifiedLinksOnly = false }: { content: string; evidence?: ResearchEvidence[]; verifiedLinksOnly?: boolean }) {
  return (
    <div className="prose prose-sm max-w-none break-words text-foreground dark:prose-invert prose-a:text-foreground prose-a:underline prose-pre:overflow-x-auto">
      <Markdown skipHtml remarkPlugins={[remarkGfm]}
        components={{
          table: ({ children }) => <div className="max-w-full overflow-x-auto" tabIndex={0} role="region" aria-label="Response table; scroll horizontally"><table>{children}</table></div>,
          pre: ({ children }) => <pre tabIndex={0} aria-label="Response code; scroll horizontally">{children}</pre>,
          img: ({ alt }) => <span>{alt}</span>,
          a: ({ href, children }) => {
            const safe = safeHref(href);
            const verified = !verifiedLinksOnly || evidence.some(source => source.sourceKind === 'public_web' && source.evidenceState === 'available' && safeHref(source.authorizedLocation ?? undefined) === safe);
            return safe && verified
              ? <a href={safe} target={safe.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{children}</a>
              : <span>{children}</span>;
          }
        }}
      >
        {citationMarkdown(content, evidence)}
      </Markdown>
    </div>
  );
}

export function ResearchMessageCard({ message }: { message: ResearchMessage }) {
  const assistant = message.role === 'assistant';
  return (
    <article
      className={assistant ? 'rounded-lg border bg-card p-4' : 'ml-auto max-w-[88%] rounded-lg bg-foreground p-4 text-background'}
      aria-label={`${assistant ? 'Researcher' : message.authorDisplayName} message`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs opacity-70">
        <span>{assistant ? 'Researcher' : message.authorDisplayName}</span>
        <time dateTime={message.createdAt}>{new Date(message.createdAt).toLocaleString()}</time>
        {message.state !== 'complete' && <Badge variant="outline">{message.state}</Badge>}
      </div>
      {assistant
        ? <ResearchMarkdown content={message.content} evidence={message.evidence} />
        : <p className="whitespace-pre-wrap text-sm">{message.content}</p>}
      {assistant && message.evidence.length > 0 && (
        <details className="mt-4 border-t pt-3 text-xs">
          <summary className="cursor-pointer font-medium">{message.evidence.length} source{message.evidence.length === 1 ? '' : 's'}</summary>
          <ol className="mt-2 space-y-2 pl-5">
            {message.evidence.map((source, index) => (
              <li key={source.id}>
                <span className="font-medium">[{index + 1}] {source.title}</span>{' '}
                <span className="text-muted-foreground">· {source.sourceKind.replace('_', ' ')} · {source.evidenceState} · retrieved {new Date(source.retrievedAt).toLocaleString()}</span>
              </li>
            ))}
          </ol>
        </details>
      )}
    </article>
  );
}
